import { useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import * as dealValidationSelectors from '../selectors/dealValidationSelectors';
import { RouteValues } from '../routeConstants';

export const useAppNavigation = (openDealRoute: string) => {
    const history = useHistory();
    const dealValidation = useSelector(dealValidationSelectors.getDealValidationState);
    const isEmployee = useSelector(dealValidationSelectors.getIsEmployee);
    const { mvDealValidation, enableRoleAssumptionInMV } = useSelector(featureToggleSelectors.getFeatureToggles);
    const isQuoteModeEnabled = useSelector(featureToggleSelectors.isManagerViewQuoteModeEnabled);
    // Check if dealership is missing in validation

    //@TODO: ML : Need to find better way to handle browser back button navigation
    // This is a temporary fix to handle the Manager View header issue when navigating back
    // Handle browser back button navigation to fix Manager View header issue
    const handlePopState = useCallback(
        (event: PopStateEvent) => {
            const currentPath = window.location.pathname;

            // Check if we're coming back to an Open Deal related page
            const isOpenDealPage = currentPath.includes('open-deal') || currentPath === '/';

            if (isOpenDealPage) {
                // Clear any Manager View state that might be persisted in the browser
                // This ensures the correct header is displayed for Open Deal pages
                if (
                    !mvDealValidation ||
                    (mvDealValidation && dealValidation?.valid) ||
                    !dealValidation ||
                    Object.keys(dealValidation).length === 0
                ) {
                    // If deal is valid, redirect to dashboard
                    history.replace(RouteValues.DASHBOARD);
                } else {
                    // If deal is invalid, allow navigation to open deal page
                    history.replace(currentPath);
                }
            }
        },
        [openDealRoute, mvDealValidation, dealValidation?.valid, history]
    );

    // Set up browser back/forward button event listener
    useEffect(() => {
        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [handlePopState]);

    // Original navigation logic
    useEffect(() => {
        // If user is an employee and dealership is missing, navigate to role assumption page
        if (isEmployee && enableRoleAssumptionInMV && mvDealValidation && !dealValidation?.valid)
            return history.push(RouteValues.ROLE_ASSUMPTION);
        if (isEmployee && !enableRoleAssumptionInMV && mvDealValidation && !dealValidation?.valid)
            return history.push(RouteValues.NO_DEALERSHIPS);
        if (!mvDealValidation || !dealValidation || Object.keys(dealValidation).length === 0)
            return history.push(RouteValues.DASHBOARD);
        if (dealValidation.valid) return history.push(RouteValues.DASHBOARD);
        // Under quote mode, Manager View handles incomplete deals in-app (a missing dealership is
        // resolved via the in-MV dealership selection; missing customer/vehicle are handled in the
        // deal itself).
        if (isQuoteModeEnabled) return history.push(RouteValues.DASHBOARD);
        history.push(openDealRoute);
    }, [dealValidation?.valid, isEmployee, enableRoleAssumptionInMV, mvDealValidation, isQuoteModeEnabled, openDealRoute, history]);
};
